import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import SectionHeading from '../components/SectionHeading';
import { productHighlights, seoByRoute, distributionPartners } from '../data/siteContent';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 1) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: 0.1 * i } }),
};

const qualityPillars = [
  {
    title: 'Rapid cold-chain logistics',
    description: 'We maintain 2-4°C temperatures from farm to shelf, preserving taste and nutrient density.',
    icon: '❄️',
  },
  {
    title: 'Rigorous quality testing',
    description: 'Daily lab benchmarks validate protein, probiotic activity, and vitamin retention across every batch.',
    icon: '✓',
  },
  {
    title: 'Circular packaging loops',
    description: 'Glass and high-density eco-packaging are collected, sanitized, and reintroduced into the cycle.',
    icon: '♻️',
  },
];

export default function Products() {
  const seo = seoByRoute['/products'];
  const { partners, distributors } = distributionPartners;

  return (
    <>
      <SEO {...seo} />
      <section className="section pt-32">
        <SectionHeading
          as="h1"
          eyebrow="Our Range"
          title="Products designed for everyday vitality"
          description="Our portfolio celebrates Sidama's fertile soils by combining slow-crafted methods with modern processing. Every bottle, cup, and pouch is optimized for nutrition and convenience."
        />

        <div className="mt-16 grid gap-10 md:grid-cols-3">
          {productHighlights.map((product, index) => (
            <motion.article
              key={product.title}
              className="flex h-full flex-col justify-between rounded-[28px] bg-white p-6 shadow-xl shadow-slate-200/70 hover:shadow-2xl hover:shadow-slate-300/50 transition-all duration-300 group border border-slate-100"
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              custom={index}
              whileHover={{ y: -8 }}
            >
              <div>
                <div className="overflow-hidden rounded-[26px] relative">
                  <img 
                    src={product.image} 
                    alt={product.title} 
                    className="h-56 w-full object-cover transition-transform duration-500 group-hover:scale-110" 
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
                <h2 className="mt-6 font-display text-2xl text-text">{product.title}</h2>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{product.description}</p>
              </div>
              <ul className="mt-6 space-y-2 text-xs uppercase tracking-wide text-slate-500">
                {product.details.map((detail) => (
                  <li key={detail} className="flex items-center gap-2">
                    <span className="inline-flex h-2 w-2 rounded-full bg-primary" />
                    {detail}
                  </li>
                ))}
              </ul>
              <a
                href="#distributor-network"
                className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-primary transition-colors hover:text-secondary"
              >
                Buy via our distributors
                <span aria-hidden="true">→</span>
              </a>
            </motion.article>
          ))}
        </div>
      </section>

      <section className="section mt-28" id="partner-network">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeUp}
        >
          <SectionHeading
            className="mb-14"
            eyebrow="Partners & collaborators"
            title="Trusted partners championing Duoley"
            description="Collaboration keeps our cold chain strong, from farm cooperatives to hotel groups showcasing our range on their menus."
          />

          <div className="grid gap-6 md:grid-cols-3">
            {partners.map((partner, index) => (
              <motion.div
                key={partner.name}
                className="rounded-3xl border border-primary/20 bg-white/80 px-6 py-6 shadow-lg shadow-primary/10 backdrop-blur-sm"
                variants={fadeUp}
                custom={index}
              >
                <h3 className="font-display text-lg text-text">{partner.name}</h3>
                <p className="mt-2 text-sm text-slate-600">{partner.focus}</p>
                <p className="mt-4 text-xs uppercase tracking-wide text-primary/80">{partner.location}</p>
                <p className="mt-3 text-sm text-slate-500">{partner.contact}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      <section className="mx-auto mt-20 max-w-6xl px-6 pb-20" id="distributor-network">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeUp}
        >
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div>
              <h2 className="font-display text-3xl text-text">Where to buy</h2>
              <p className="mt-3 max-w-2xl text-sm text-slate-600">
                Contact any of our regional distributors below to place volume orders, restock retail shelves, or secure
                hospitality supply. They maintain Duoley’s cold-chain integrity from our plant to your door.
              </p>
            </div>
            <Link to="/contact" className="btn-primary inline-flex w-fit">
              Become a distributor
            </Link>
          </div>

          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {distributors.map((distributor, index) => (
              <motion.article
                key={distributor.name}
                className="rounded-3xl border border-slate-200 bg-white p-6 shadow-lg shadow-slate-200/60 hover:shadow-2xl hover:shadow-primary/20 transition-all"
                variants={fadeUp}
                custom={index}
              >
                <h3 className="font-display text-lg text-text">{distributor.name}</h3>
                <p className="mt-2 text-sm text-slate-600">{distributor.coverage}</p>
                <div className="mt-4 space-y-1 text-sm text-slate-500">
                  <div>{distributor.contact}</div>
                  <div>{distributor.phone}</div>
                </div>
              </motion.article>
            ))}
          </div>
        </motion.div>
      </section>

      <section className="mx-auto mt-32 max-w-6xl px-6">
        <motion.div
          className="rounded-[32px] bg-gradient-to-br from-white via-slate-50 to-white px-10 py-12 shadow-2xl shadow-slate-200/50 border border-slate-200"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.35 }}
          variants={fadeUp}
        >
          <div className="grid gap-12 lg:grid-cols-[1.2fr_0.8fr]">
            <div>
              <h2 className="font-display text-3xl text-text">Built for retail shelves and hospitality menus</h2>
              <p className="mt-4 text-sm leading-7 text-slate-600">
                We offer flexible packaging, single-serve, family packs, and catering formats, that retain cold-chain
                compliance. Custom flavor collaborations and limited seasonal runs are available for partners looking to
                delight customers with signature experiences.
              </p>
              <div className="mt-8 grid gap-6 md:grid-cols-3">
                {qualityPillars.map((pillar, idx) => (
                  <motion.div 
                    key={pillar.title} 
                    className="rounded-3xl border border-primary/20 bg-gradient-to-br from-primary/5 to-transparent p-5 text-sm text-slate-600 hover:border-primary/40 transition-all"
                    variants={fadeUp}
                    custom={idx}
                  >
                    <div className="text-2xl mb-2">{pillar.icon}</div>
                    <p className="font-display text-base text-text">{pillar.title}</p>
                    <p className="mt-3 leading-6">{pillar.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
            <div className="space-y-4 text-sm text-slate-600">
              <h3 className="font-display text-lg text-text">Flavor profiles & pairings</h3>
              <motion.div
                className="rounded-3xl bg-gradient-to-br from-primary/10 to-transparent p-5 border border-primary/20"
                variants={fadeUp}
              >
                <p><span className="font-semibold text-primary">Signature Milk:</span> Balanced sweetness ideal for steaming,
                cereal, and wholesome beverages.</p>
              </motion.div>
              <motion.div
                className="rounded-3xl bg-gradient-to-br from-primary/10 to-transparent p-5 border border-primary/20"
                variants={fadeUp}
                custom={1}
              >
                <p><span className="font-semibold text-primary">Duoley Yogurt:</span> Thick, spoonable textures that partner
                beautifully with granola, smoothies, and savory dips.</p>
              </motion.div>
              <motion.div
                className="rounded-3xl bg-gradient-to-br from-primary/10 to-transparent p-5 border border-primary/20"
                variants={fadeUp}
                custom={2}
              >
                <p><span className="font-semibold text-primary">Juice Blends:</span> Pineapple-kale, mango-ginger, and
                strawberry-guava combinations to refresh cafés, co-working spaces, and hotels.</p>
              </motion.div>
              <motion.div
                className="rounded-3xl bg-gradient-to-br from-primary to-primary/80 p-6 text-white"
                variants={fadeUp}
                custom={3}
              >
                <p className="text-xs uppercase tracking-[0.35em] text-white/70 font-semibold">Partner perk</p>
                <p className="mt-3 text-white">
                  Exclusive tasting workshops and staff training help your team speak confidently about every Duoley
                  product.
                </p>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
}
